import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardHeader from "../../components/app_components/DashboardHeader";
import DashboardSidebar from "../../components/app_components/DashboardSidebar";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Home,
  Bell,
  BellDot,
  ShoppingBag,
  Settings,
  Users,
  ShoppingCart,
  Loader2,
  Share2,
} from "lucide-react";
import { useTranslation } from "../../contexts/LanguageContext";
import { useAuth } from "../../App"; 

const MarketplaceCategory = () => {
  const navigate = useNavigate();
  const { category } = useParams();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Decode category for display
  const decodedCategory = decodeURIComponent(category || '');

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate("/demo");
    }
  }, [user, navigate]);

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      if (!user) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchUnreadNotifications();
        }
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err.status === 401) {
        navigate("/demo");
      }
    }
  }, [user, httpClient, validateToken, navigate]);
  
  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
    }
  }, [user, fetchUnreadNotifications]);

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };
  
  const navItems = [
    { icon: Home, label: t('bottomNav.home') || "Home", path: "/dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications') || "Notifications",
      path: "/notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace') || "Marketplace", path: "/marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs') || "Share Subs", path: "/share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank') || "Connect Bank", path: "/connectBank" },
    { icon: Users, label: t('bottomNav.partners') || "Partners", path: "/partners" },
    { icon: Settings, label: t('bottomNav.settings') || "Settings", path: "/account-settings" },
  ];

  // Fetch merchants by category
  const fetchMerchantsByCategory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // IMPORTANT: Use httpClient which handles URL construction properly
      const response = await httpClient.request(`/api/marketplace-category?category=${encodeURIComponent(decodedCategory)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setMerchants(data.merchants || []);
      } else {
        throw new Error(data.error || "Failed to load category");
      }
    } catch (err) {
      setError(err.message || 'Failed to load category');
      console.error("Category fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [decodedCategory, httpClient]);

  useEffect(() => {
    if (user && decodedCategory) {
      fetchMerchantsByCategory();
      fetchPremiumStatus(); // Fetch premium status
    }
  }, [user, decodedCategory, fetchMerchantsByCategory]);

  // Ad component
  const AdBanner = () => (
    <div className="bg-white rounded-xl p-4 my-6 shadow-sm border border-blue-100">
      <h3 className="text-sm font-medium text-gray-500 mb-2">{t('marketplaceCategory.sponsored') || "Sponsored"}</h3>
      <a
        rel="sponsored"
        href="https://www.awin1.com/cread.php?s=3975182&v=115715&q=516084&r=1966723"
        className="block"
      >
        <img
          src="https://www.awin1.com/cshow.php?s=3975182&v=115715&q=516084&r=1966723"
          alt="eSIM for International Travel"
          className="w-full rounded-lg"
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/800x200?text=Sponsored+Ad";
          }}
        />
      </a>
    </div>
  );

  // Format price for display
  const formatPrice = (price) => {
    return price % 1 === 0 ? price : price.toFixed(2);
  };

  // Pagination logic
  const filteredMerchants = merchants.filter((merchant) =>
    merchant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMerchants = filteredMerchants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render pagination controls
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center items-center mt-8 space-x-2">
        <button
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg ${
            currentPage === 1 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white text-[#253985] hover:bg-gray-100'
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => paginate(number)}
            className={`w-8 h-8 rounded-full text-sm ${
              currentPage === number
                ? 'bg-[#253985] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {number}
          </button>
        ))}
        
        <button
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white text-[#253985] hover:bg-gray-100'
          }`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <DashboardSidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        navItems={navItems}
        currentPath={window.location.pathname}
        navigate={navigate}
        user={user}
        onLogout={logout}
      />

      <div className="flex-1 overflow-auto">
        <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />

        <div className="p-6 space-y-6">
          {/* Informative Header Block with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-6 rounded-lg text-center transition-all duration-500`}>
            <h1 className="text-xl font-semibold mb-2">{t('marketplace.title') || "Subscription Marketplace"}</h1>
            <p className="text-white">{t('marketplace.subtitle') || "Discover and manage your favorite subscriptions"}</p>
          </div>

          <div className="flex items-center">
            <button
              onClick={() => navigate("/marketplace")}
              className="flex items-center text-[#253985] hover:text-[#1a2c6d]"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              {t('marketplaceCategory.backToMarketplace') || "Back to Marketplace"}
            </button>
            <h1 className="text-2xl font-bold ml-4 text-gray-800">
              {decodedCategory}
            </h1>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={(t('marketplaceCategory.searchPlaceholder') || "Search in") + " " + decodedCategory + "..."}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Content Area */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium">{t('marketplaceCategory.loadErrorTitle') || "Error loading category"}</h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={fetchMerchantsByCategory}
                className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                {t('marketplace.tryAgain') || "Try again"}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentMerchants.map((merchant) => (
                  <div
                    key={merchant.id}
                    onClick={() => navigate(`/merchant/${merchant.id}`)}
                    className="border border-[#253985]/20 rounded-xl overflow-hidden hover:shadow-lg transition-all bg-white cursor-pointer group"
                  >
                    <div className="p-5 flex flex-col h-full">
                      <div className="flex justify-center mb-4">
                        <img
                          src={merchant.logo}
                          alt={merchant.name}
                          className="w-20 h-20 rounded-lg object-contain p-1 border border-[#253985]/10"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/80";
                          }}
                        />
                      </div>

                      <div className="flex-1 space-y-2">
                        <h3 className="font-medium text-[#253985] text-center mb-1 text-lg group-hover:text-[#1a2c6d]">
                          {merchant.name}
                        </h3>

                        {merchant.description && (
                          <p className="text-sm text-gray-600 text-center line-clamp-3">
                            {merchant.description}
                          </p>
                        )}

                        {merchant.plans && merchant.plans.length > 0 && (
                          <div className="mt-3 text-center">
                            <span className="inline-block bg-[#253985]/10 text-[#253985] px-3 py-1 rounded-full text-sm font-medium">
                              {t('marketplace.from') || "From"} {merchant.plans[0].currency}{formatPrice(
                                Math.min(...merchant.plans.map((p) => p.price || 0))
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/merchant/${merchant.id}`);
                        }}
                        className="mt-4 w-full py-2 bg-[#253985] hover:bg-[#1a2c6d] text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {t('marketplace.moreDetails') || "More Details"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredMerchants.length === 0 && !loading && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-lg">{t('marketplaceCategory.noMerchantsFound') || "No merchants found in this category"}</p>
                  <button
                    onClick={() => navigate("/marketplace")}
                    className="mt-4 bg-[#253985] hover:bg-[#1a2c6d] text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {t('marketplaceCategory.browseAll') || "Browse All Categories"}
                  </button>
                </div>
              )}
              
              {/* Pagination Controls */}
              {filteredMerchants.length > 0 && <Pagination />}
              
              {/* Ad Banner at the bottom */}
              {filteredMerchants.length > 0 && <AdBanner />}
              
              {/* Results count */}
              {filteredMerchants.length > 0 && (
                <div className="text-center text-gray-500 text-sm mt-4">
                  {Math.min(indexOfFirstItem + 1, filteredMerchants.length)}-{Math.min(indexOfLastItem, filteredMerchants.length)} {t('marketplaceCategory.of') || "of"} {filteredMerchants.length} {t('marketplaceCategory.results') || "results"}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceCategory;